import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import type { ProgrammeSlide } from "../content/programme-galleries";

type ProgrammeGalleryProps = {
  title: string;
  slides: ProgrammeSlide[];
};

export function ProgrammeGallery({ title, slides }: ProgrammeGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  if (slides.length === 0) return null;

  const showSlide = (index: number) => {
    setActiveIndex((index + slides.length) % slides.length);
  };

  const changeSlide = (direction: -1 | 1) => showSlide(activeIndex + direction);

  return (
    <section
      className="programme-gallery"
      role="region"
      aria-roledescription="carousel"
      aria-label={`${title} photographs`}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          changeSlide(-1);
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          changeSlide(1);
        }
      }}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const start = touchStartX.current;
        const end = event.changedTouches[0]?.clientX;
        touchStartX.current = null;
        if (start == null || end == null || Math.abs(start - end) < 48) return;
        changeSlide(start > end ? 1 : -1);
      }}
    >
      <div className="gallery-stage">
        {slides.map((slide, index) => (
          <figure className="gallery-slide" hidden={index !== activeIndex} key={slide.src}>
            <img
              src={slide.src}
              alt={slide.alt}
              width={slide.width}
              height={slide.height}
              loading="lazy"
              decoding="async"
            />
            <figcaption>{slide.caption}</figcaption>
          </figure>
        ))}
      </div>

      <div className={slides.length > 6 ? "gallery-navigation many-slides" : "gallery-navigation"}>
        {slides.length > 1 && (
          <div className="gallery-buttons">
            <button
              type="button"
              onClick={() => changeSlide(-1)}
              aria-label={`Previous image in ${title} gallery`}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => changeSlide(1)}
              aria-label={`Next image in ${title} gallery`}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        )}

        <div className="gallery-dots" role="group" aria-label="Choose a photograph">
          {slides.map((slide, index) => (
            <button
              type="button"
              className={index === activeIndex ? "is-active" : undefined}
              aria-label={`Show image ${index + 1} of ${slides.length}: ${slide.caption}`}
              aria-current={index === activeIndex ? "true" : undefined}
              onClick={() => showSlide(index)}
              key={slide.src}
            />
          ))}
        </div>

        <p className="gallery-counter" aria-live="polite" aria-atomic="true">
          <span>{String(activeIndex + 1).padStart(2, "0")}</span>
          <span aria-hidden="true"> / </span>
          {String(slides.length).padStart(2, "0")}
        </p>
      </div>

      <a className="gallery-original" href={slides[activeIndex].src} target="_blank" rel="noreferrer"><Expand size={14} aria-hidden="true" /> Open full-size photograph</a>
    </section>
  );
}
